import React, { useRef, useEffect, useState } from 'react';
import Map, { MapRef, Marker, Source, Layer } from 'react-map-gl/mapbox';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, IconButton, Tooltip, Paper, Stack } from '@mui/material';
import { Layers, Satellite, MyLocation, LocationOn } from '@mui/icons-material';
import EnhancedPermanentLayers from '../EnhancedPermanentLayers';
import { useMap, BasemapType } from '../../context/MapContext';
import { LayerConfig } from '../../data/layersConfig';

const MAPBOX_TOKEN = "pk.eyJ1IjoiZW1pbmJvdWthIiwiYSI6ImNtZGdvaXp3MzAyZmgybXM2MWEwNnc4cGQifQ.qUIaXbqY_p32IEGF3vaXhA";

interface PublicMapComponentProps {
    layers: LayerConfig[];
    center: [number, number];
    zoom: number;
    flyToTarget?: { lat: number; lng: number } | null;
    flyToTrigger?: boolean;
    flyToZoom?: number | null;
    onMapLoad?: (map: mapboxgl.Map) => void;
}

const PublicMapComponent: React.FC<PublicMapComponentProps> = ({
    layers,
    center,
    zoom,
    flyToTarget,
    flyToTrigger,
    flyToZoom,
    onMapLoad
}) => {
    const mapRef = useRef<MapRef>(null);
    const { basemap, setBasemap, visibleLayers } = useMap();
    const [viewState, setViewState] = useState({
        longitude: center[0],
        latitude: center[1],
        zoom: zoom
    });
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const [destination, setDestination] = useState<[number, number] | null>(null);
    const [routeData, setRouteData] = useState<any>(null);

    const getBasemapStyle = (type: BasemapType): string => {
        switch (type) {
            case 'mapbox-streets':
                return 'mapbox://styles/mapbox/streets-v12';
            case 'satellite-a':
            case 'satellite-b':
            case 'satellite-c':
            case 'satellite-enhanced':
                return 'mapbox://styles/mapbox/satellite-streets-v12';
            default:
                return 'mapbox://styles/mapbox/streets-v12';
        }
    };

    const toggleBasemap = () => {
        if (basemap === 'mapbox-streets') {
            setBasemap('satellite-enhanced');
        } else {
            setBasemap('mapbox-streets');
        }
    };

    const getUserLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setUserLocation([longitude, latitude]);
                    const map = mapRef.current?.getMap();
                    if (map) {
                        map.flyTo({
                            center: [longitude, latitude],
                            zoom: 15,
                            essential: true
                        });
                    }
                },
                (error) => {
                    console.error('Error getting location:', error);
                }
            );

            navigator.geolocation.watchPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setUserLocation([longitude, latitude]);
                },
                (error) => {
                    console.error('Error watching location:', error);
                }
            );
        } else {
            console.error('Geolocation is not supported by this browser.');
        }
    };

    const fetchRoute = async (start: [number, number], end: [number, number]) => {
        try {
            const response = await fetch(
                `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?geometries=geojson&exclude=ferry&access_token=${MAPBOX_TOKEN}`
            );
            const data = await response.json();

            if (data.routes && data.routes.length > 0) {
                const route = data.routes[0];

                if (route.geometry && route.geometry.coordinates) {
                    const coordinates = route.geometry.coordinates;
                    const safetyMargin = 0.2;
                    const mauritaniaBounds = {
                        minLng: -16.8 + safetyMargin,
                        maxLng: -5.0 - safetyMargin,
                        minLat: 14.9 + safetyMargin,
                        maxLat: 27.1 - safetyMargin
                    };

                    const hasInvalidPoints = coordinates.some((coord: [number, number]) => {
                        const [lng, lat] = coord;
                        return lng < mauritaniaBounds.minLng ||
                            lng > mauritaniaBounds.maxLng ||
                            lat < mauritaniaBounds.minLat ||
                            lat > mauritaniaBounds.maxLat;
                    });

                    if (hasInvalidPoints) {
                        console.warn('Route crosses or approaches borders - rejecting');
                        setRouteData(null);
                        return;
                    }
                }

                setRouteData({
                    type: 'Feature',
                    properties: {},
                    geometry: route.geometry
                });
            } else {
                console.warn('No route found for destination');
                setRouteData(null);
            }
        } catch (error) {
            console.error('Error fetching route:', error);
            setRouteData(null);
        }
    };

    const handleMapClick = (event: any) => {
        const { lngLat } = event;
        const clickedPoint: [number, number] = [lngLat.lng, lngLat.lat];

        const mauritaniaBounds = {
            minLng: -17.0,
            maxLng: -4.8,
            minLat: 14.7,
            maxLat: 27.3
        };

        const isWithinMauritania =
            clickedPoint[0] >= mauritaniaBounds.minLng &&
            clickedPoint[0] <= mauritaniaBounds.maxLng &&
            clickedPoint[1] >= mauritaniaBounds.minLat &&
            clickedPoint[1] <= mauritaniaBounds.maxLat;

        if (!isWithinMauritania) {
            console.warn('Click outside Mauritania borders - ignoring');
            return;
        }

        setDestination(clickedPoint);

        if (userLocation) {
            fetchRoute(userLocation, clickedPoint);
        }
    };

    useEffect(() => {
        const map = mapRef.current?.getMap();
        if (map && flyToTarget && flyToTrigger) {
            map.flyTo({
                center: [flyToTarget.lng, flyToTarget.lat],
                zoom: flyToZoom ?? 15,
                essential: true
            });
        }
    }, [flyToTarget, flyToTrigger, flyToZoom]);

    return (
        <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            <Map
                ref={mapRef}
                mapboxAccessToken={MAPBOX_TOKEN}
                initialViewState={viewState}
                style={{ width: '100%', height: '100%' }}
                mapStyle={getBasemapStyle(basemap)}
                onLoad={(e) => onMapLoad?.(e.target)}
                onMove={(evt) => setViewState(evt.viewState)}
                onClick={handleMapClick}
                attributionControl={false}
            >
                <EnhancedPermanentLayers
                    layers={layers}
                    visibleLayers={visibleLayers}
                />

                {userLocation && (
                    <Marker longitude={userLocation[0]} latitude={userLocation[1]}>
                        <Box
                            sx={{
                                width: 16,
                                height: 16,
                                borderRadius: '50%',
                                backgroundColor: '#4285F4',
                                border: '3px solid white',
                                boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
                            }}
                        />
                    </Marker>
                )}

                {destination && (
                    <Marker longitude={destination[0]} latitude={destination[1]}>
                        <LocationOn sx={{ fontSize: 40, color: '#EA4335' }} />
                    </Marker>
                )}

                {routeData && (
                    <Source id="route" type="geojson" data={routeData}>
                        <Layer
                            id="route-layer"
                            type="line"
                            paint={{
                                'line-color': '#4285F4',
                                'line-width': 4,
                                'line-opacity': 0.8
                            }}
                        />
                    </Source>
                )}
            </Map>

            <Paper
                elevation={3}
                sx={{
                    position: 'absolute',
                    bottom: 100,
                    right: 10,
                    zIndex: 1,
                    borderRadius: '24px',
                    overflow: 'hidden'
                }}
            >
                <Stack spacing={0}>
                    <Tooltip title="My Current Position" placement="left">
                        <IconButton onClick={getUserLocation} sx={{ width: 48, height: 48 }}>
                            <MyLocation sx={{ color: userLocation ? '#4285F4' : 'inherit' }} />
                        </IconButton>
                    </Tooltip>
                    <Box sx={{ borderTop: 1, borderColor: 'divider' }} />
                    <Tooltip title={basemap === 'mapbox-streets' ? "Satellite View" : "Street View"} placement="left">
                        <IconButton onClick={toggleBasemap} sx={{ width: 48, height: 48 }}>
                            {basemap === 'mapbox-streets' ? <Satellite /> : <Layers />}
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Paper>
        </Box>
    );
};

export default PublicMapComponent;
